/**
 * `/$tenantSlug/settings/security` — personal account security
 * (mfa_two_factor, Wave 2 slice 3).
 *
 * Builds the previously-DEAD Security route. The only setting here today is
 * TOTP two-factor.
 *
 * PERMISSION NUANCE (deliberate): two-factor is a PERSONAL account setting —
 * any signed-in user (including an advisor) must be able to turn 2FA on for
 * their OWN account. So this route is reachable by ANY authenticated user and
 * is NOT gated on `shell.settings.view` (unlike the tenant-config children
 * such as Organization/Modules). The Security card in the settings index is
 * likewise visible to everyone.
 *
 * State A — 2FA OFF: an "Enable two-factor" button opens the EnrollDialog
 *   wizard: (1) scan QR / copy secret, (2) verify a live 6-digit code,
 *   (3) save recovery codes (shown once, gated by a required confirm box).
 * State B — 2FA ON: a "Two-factor is on" status badge + Disable (requires a
 *   current code or password) + Regenerate recovery codes (re-runs the
 *   shown-once panel; old codes are invalidated server-side).
 *
 * The initial on/off comes from the session's `mfa_enabled` (identity-level).
 * After a successful enable/disable we flip a local override so the page
 * reflects the change immediately without waiting for a session refresh.
 *
 * qrcode.react is lazy-loaded (React.lazy) so it only enters the bundle on
 * this route. The otpauth:// URI is SERVER-generated; we only render it.
 *
 * A11y (a11y contract):
 *   - The QR <img>/<svg> is aria-hidden with empty alt; the secret is ALWAYS
 *     offered as selectable mono text + a Copy button (parity with team.tsx).
 *   - Recovery codes live in a labelled region with the "shown once" warning
 *     as real text + a REQUIRED confirm checkbox gating Done; focus moves to
 *     the panel heading on reveal.
 *   - Errors render in a role=alert that receives focus only on failure.
 *   - The enroll dialog traps focus (Radix) and restores to the opener on
 *     close. No extra global Esc handler.
 */

import { useMfaEnabled } from "@ken/auth";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  StatusBadge,
  Skeleton,
} from "@ken/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Copy, ShieldCheck } from "lucide-react";
import {
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";

import { OtpCodeInput } from "../components/OtpCodeInput";

// Lazy — qrcode.react only loads on this route (bundle hygiene, ADR-030 §3.1).
const QRCodeSVG = lazy(async () => {
  const mod = await import("qrcode.react");
  return { default: mod.QRCodeSVG };
});

export const Route = createFileRoute("/$tenantSlug/settings/security")({
  component: SecuritySettingsRoute,
});

// ---------------------------------------------------------------------------
// Backend contract (mfa_two_factor) — consumed, not built here.
//   POST /api/shell/auth/mfa/enroll/initiate -> {secret, otpauth_uri}
//   POST /api/shell/auth/mfa/enroll/verify   {code} -> {recovery_codes}
//   POST /api/shell/auth/mfa/disable         {code|password} -> 204
//   POST /api/shell/auth/mfa/recovery/regenerate {code} -> {recovery_codes}
// ---------------------------------------------------------------------------

interface InitiateResponse {
  secret: string;
  otpauth_uri: string;
}

interface RecoveryResponse {
  recovery_codes: string[];
}

function SecuritySettingsRoute(): ReactElement {
  // Initial on/off from the identity-level session fact.
  const sessionEnabled = useMfaEnabled();
  // Local override so the UI reflects an enable/disable immediately.
  const [override, setOverride] = useState<boolean | null>(null);
  const enabled = override ?? sessionEnabled;

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  // When set, the RecoveryCodesPanel is shown for a regeneration (State B)
  // rather than inside the enrollment wizard.
  const [regenCodes, setRegenCodes] = useState<string[] | null>(null);

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="shell-settings-security-page"
    >
      <header>
        <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold">
          Account · Settings
        </div>
        <h1 className="text-[26px] font-bold tracking-tight">Security</h1>
        <p className="text-sm text-[var(--color-text-soft)] mt-1">
          Protect your account with two-factor authentication. This setting is
          personal — it applies to your sign-in everywhere, not just this
          company.
        </p>
      </header>

      <section aria-labelledby="mfa-heading" className="flex flex-col gap-3">
        <h2 id="mfa-heading" className="text-[18px] font-semibold">
          Two-factor authentication
        </h2>

        <Card surface="glass-strong">
          <CardContent>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <ShieldCheck
                    size={18}
                    aria-hidden="true"
                    className="text-[var(--color-text-soft)] shrink-0"
                  />
                  {enabled ? (
                    <StatusBadge
                      status="active"
                      label="Two-factor is on"
                      data-testid="shell-settings-security-status"
                    />
                  ) : (
                    <StatusBadge
                      status="inert"
                      label="Two-factor is off"
                      data-testid="shell-settings-security-status"
                    />
                  )}
                </div>
                <p className="text-sm text-[var(--color-text-soft)] mt-2">
                  {enabled
                    ? "An authenticator app code is required after your password each time you sign in."
                    : "Add a one-time code from an authenticator app (Google Authenticator, 1Password, Microsoft Authenticator) on top of your password."}
                </p>
              </div>

              <div className="shrink-0 flex flex-col gap-2">
                {enabled ? (
                  <>
                    <Button
                      type="button"
                      variant="solid"
                      size="md"
                      data-testid="shell-settings-security-regenerate-button"
                      onClick={() => setRegenCodes([])}
                    >
                      Regenerate recovery codes
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="md"
                      data-testid="shell-settings-security-disable-button"
                      onClick={() => setDisableOpen(true)}
                    >
                      Turn off
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    data-testid="shell-settings-security-enable-button"
                    onClick={() => setEnrollOpen(true)}
                  >
                    Enable two-factor
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Enrollment wizard (State A -> on). */}
      <EnrollDialog
        open={enrollOpen}
        onOpenChange={setEnrollOpen}
        onEnabled={() => setOverride(true)}
      />

      {/* Disable confirm (State B -> off). */}
      <DisableDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        onDisabled={() => setOverride(false)}
      />

      {/* Regenerate recovery codes (State B). Re-uses the shown-once panel in
          its own dialog so the same required-confirm gate applies. */}
      <RegenerateDialog
        open={regenCodes !== null}
        onOpenChange={(o) => {
          if (!o) setRegenCodes(null);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recovery codes panel — shown ONCE, copyable, confirm-gated.
// ---------------------------------------------------------------------------

function RecoveryCodesPanel({
  codes,
  onDone,
  doneTestId,
  confirmTestId,
  copyTestId,
  listTestId,
}: {
  codes: string[];
  onDone: () => void;
  doneTestId: string;
  confirmTestId: string;
  copyTestId: string;
  listTestId: string;
}): ReactElement {
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // Move focus to the panel heading on reveal (a11y contract).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  async function copyCodes(): Promise<void> {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
    } catch {
      // Clipboard may be unavailable; the codes are selectable as a fallback.
      setCopied(false);
    }
  }

  return (
    <section
      aria-labelledby="recovery-codes-heading"
      className="flex flex-col gap-3"
    >
      <h3
        id="recovery-codes-heading"
        ref={headingRef}
        tabIndex={-1}
        className="text-[16px] font-semibold focus-visible:outline-none"
      >
        Save your recovery codes
      </h3>
      <p className="text-sm text-[var(--color-status-overdue-text)] font-medium">
        These codes are shown once and won&apos;t be displayed again. Store them
        somewhere safe — each one lets you sign in if you lose your
        authenticator. Generating new codes invalidates these.
      </p>
      <ul
        data-testid={listTestId}
        className="select-all break-all grid grid-cols-2 gap-1.5 rounded-[var(--radius-sm)] border border-[var(--color-border-hairline)] bg-[var(--color-surface-soft)] px-3 py-3 text-[13px] font-mono text-[var(--color-text-strong)]"
      >
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid={copyTestId}
          onClick={() => void copyCodes()}
        >
          {copied ? (
            <>
              <Check size={16} aria-hidden="true" />
              Copied
            </>
          ) : (
            <>
              <Copy size={16} aria-hidden="true" />
              Copy codes
            </>
          )}
        </Button>
      </div>
      <label className="flex items-start gap-2 text-sm text-[var(--color-text-strong)] cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          data-testid={confirmTestId}
          className="accent-[var(--color-brand)] w-4 h-4 shrink-0 mt-0.5"
        />
        <span>I&apos;ve saved these codes somewhere safe.</span>
      </label>
      <DialogFooter>
        <Button
          type="button"
          variant="primary"
          size="md"
          data-testid={doneTestId}
          disabled={!confirmed}
          onClick={onDone}
        >
          Done
        </Button>
      </DialogFooter>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Enrollment wizard: QR -> verify -> recovery codes.
// ---------------------------------------------------------------------------

type EnrollStep = "qr" | "verify" | "recovery";

function EnrollDialog({
  open,
  onOpenChange,
  onEnabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEnabled: () => void;
}): ReactElement {
  const [step, setStep] = useState<EnrollStep>("qr");
  const [initiate, setInitiate] = useState<InitiateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [secretCopied, setSecretCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  // Kick off enrollment whenever the dialog opens. Reset everything on close.
  useEffect(() => {
    if (!open) {
      setStep("qr");
      setInitiate(null);
      setCode("");
      setRecoveryCodes([]);
      setSecretCopied(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await fetch("/api/shell/auth/mfa/enroll/initiate", {
          method: "POST",
          credentials: "include",
        });
        if (cancelled) return;
        if (!res.ok) {
          setError(`Couldn't start enrollment (status ${res.status}).`);
          return;
        }
        setInitiate((await res.json()) as InitiateResponse);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function copySecret(): Promise<void> {
    if (!initiate) return;
    try {
      await navigator.clipboard.writeText(initiate.secret);
      setSecretCopied(true);
    } catch {
      setSecretCopied(false);
    }
  }

  async function onVerify(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/shell/auth/mfa/enroll/verify", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (res.status === 401 || res.status === 400) {
        setError("That code didn't match. Check your app and try again.");
        return;
      }
      if (!res.ok) {
        setError(`Couldn't turn on two-factor (status ${res.status}).`);
        return;
      }
      const body = (await res.json()) as RecoveryResponse;
      setRecoveryCodes(body.recovery_codes);
      onEnabled();
      setStep("recovery");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shell-settings-security-enroll-dialog">
        <DialogHeader>
          <DialogTitle>
            {step === "recovery"
              ? "Two-factor is on"
              : "Set up two-factor authentication"}
          </DialogTitle>
          <DialogDescription>
            {step === "qr" &&
              "Scan this code with your authenticator app, or enter the key by hand."}
            {step === "verify" &&
              "Enter the 6-digit code your app shows to confirm it's working."}
            {step === "recovery" &&
              "Save these recovery codes before you finish."}
          </DialogDescription>
        </DialogHeader>

        {error !== null && (
          <div
            ref={errorRef}
            tabIndex={-1}
            role="alert"
            data-testid="shell-settings-security-error"
            className="text-sm text-[var(--color-status-overdue-text)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-status-overdue-bg)] border border-[var(--color-status-overdue-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-status-overdue-text)]"
          >
            {error}
          </div>
        )}

        {step === "qr" && (
          <div className="flex flex-col gap-4">
            {loading || !initiate ? (
              <Skeleton className="h-44 w-44 mx-auto rounded-[var(--radius-md)]" />
            ) : (
              <>
                <div
                  className="mx-auto rounded-[var(--radius-md)] bg-white p-3 border border-[var(--color-border-hairline)]"
                  data-testid="shell-settings-security-qr"
                  aria-hidden="true"
                >
                  <Suspense
                    fallback={
                      <Skeleton className="h-40 w-40 rounded-[var(--radius-sm)]" />
                    }
                  >
                    <QRCodeSVG
                      value={initiate.otpauth_uri}
                      size={160}
                      // The QR is a convenience; the secret below is the
                      // accessible equivalent. Hidden from assistive tech.
                      aria-hidden="true"
                    />
                  </Suspense>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[12px] font-semibold text-[var(--color-text-strong)]">
                    Can&apos;t scan? Enter this key:
                  </span>
                  <div className="flex items-center gap-2">
                    <code
                      data-testid="shell-settings-security-secret"
                      className="select-all break-all flex-1 rounded-[var(--radius-sm)] border border-[var(--color-border-hairline)] bg-[var(--color-surface-soft)] px-3 py-2 text-[13px] font-mono text-[var(--color-text-strong)]"
                    >
                      {initiate.secret}
                    </code>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid="shell-settings-security-secret-copy"
                      onClick={() => void copySecret()}
                    >
                      {secretCopied ? (
                        <>
                          <Check size={16} aria-hidden="true" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy size={16} aria-hidden="true" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    data-testid="shell-settings-security-qr-continue"
                    onClick={() => {
                      setError(null);
                      setStep("verify");
                    }}
                  >
                    Next
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}

        {step === "verify" && (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => void onVerify(e)}
            noValidate
          >
            <OtpCodeInput
              id="mfa-enroll-code"
              label="Authenticator code"
              hint="6 digits from your app."
              value={code}
              onChange={setCode}
              invalid={error !== null}
              disabled={submitting}
              autoFocus
              data-testid="shell-settings-security-code-input"
            />
            <DialogFooter>
              <Button
                type="submit"
                variant="primary"
                size="md"
                data-testid="shell-settings-security-verify-submit"
                disabled={submitting}
              >
                {submitting ? "Verifying…" : "Verify & turn on"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === "recovery" && (
          <RecoveryCodesPanel
            codes={recoveryCodes}
            onDone={() => onOpenChange(false)}
            listTestId="shell-settings-security-recovery-codes"
            copyTestId="shell-settings-security-recovery-copy"
            confirmTestId="shell-settings-security-recovery-confirm"
            doneTestId="shell-settings-security-recovery-done"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Disable dialog — requires a current code or password.
// ---------------------------------------------------------------------------

function DisableDialog({
  open,
  onOpenChange,
  onDisabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDisabled: () => void;
}): ReactElement {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      setCode("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  async function onConfirm(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (code.length !== 6) {
      setError("Enter a current 6-digit code to turn off two-factor.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/shell/auth/mfa/disable", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (res.status === 401 || res.status === 400) {
        setError("That code didn't match. Try again.");
        return;
      }
      if (!res.ok && res.status !== 204) {
        setError(`Couldn't turn off two-factor (status ${res.status}).`);
        return;
      }
      onDisabled();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shell-settings-security-disable-dialog">
        <DialogHeader>
          <DialogTitle>Turn off two-factor?</DialogTitle>
          <DialogDescription>
            Your account will be protected by your password alone. Enter a
            current authenticator code to confirm it&apos;s you.
          </DialogDescription>
        </DialogHeader>

        {error !== null && (
          <div
            ref={errorRef}
            tabIndex={-1}
            role="alert"
            data-testid="shell-settings-security-disable-error"
            className="text-sm text-[var(--color-status-overdue-text)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-status-overdue-bg)] border border-[var(--color-status-overdue-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-status-overdue-text)]"
          >
            {error}
          </div>
        )}

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => void onConfirm(e)}
          noValidate
        >
          <OtpCodeInput
            id="mfa-disable-code"
            label="Current authenticator code"
            value={code}
            onChange={setCode}
            invalid={error !== null}
            disabled={submitting}
            autoFocus
            data-testid="shell-settings-security-disable-code"
          />
          <DialogFooter>
            <Button
              type="submit"
              variant="primary"
              size="md"
              data-testid="shell-settings-security-disable-confirm"
              disabled={submitting}
            >
              {submitting ? "Turning off…" : "Turn off two-factor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Regenerate-recovery-codes dialog — requires a current code, then shows the
// new codes once (re-uses RecoveryCodesPanel + its confirm gate).
// ---------------------------------------------------------------------------

function RegenerateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      setCode("");
      setError(null);
      setSubmitting(false);
      setCodes(null);
    }
  }, [open]);

  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  async function onRegenerate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (code.length !== 6) {
      setError("Enter a current 6-digit code to generate new recovery codes.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/shell/auth/mfa/recovery/regenerate", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (res.status === 401 || res.status === 400) {
        setError("That code didn't match. Try again.");
        return;
      }
      if (!res.ok) {
        setError(`Couldn't regenerate codes (status ${res.status}).`);
        return;
      }
      const body = (await res.json()) as RecoveryResponse;
      setCodes(body.recovery_codes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shell-settings-security-regenerate-dialog">
        <DialogHeader>
          <DialogTitle>
            {codes ? "Your new recovery codes" : "Regenerate recovery codes"}
          </DialogTitle>
          <DialogDescription>
            {codes
              ? "Save these new codes — your old recovery codes no longer work."
              : "This invalidates your existing recovery codes. Enter a current authenticator code to confirm."}
          </DialogDescription>
        </DialogHeader>

        {error !== null && (
          <div
            ref={errorRef}
            tabIndex={-1}
            role="alert"
            data-testid="shell-settings-security-regenerate-error"
            className="text-sm text-[var(--color-status-overdue-text)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-status-overdue-bg)] border border-[var(--color-status-overdue-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-status-overdue-text)]"
          >
            {error}
          </div>
        )}

        {codes ? (
          <RecoveryCodesPanel
            codes={codes}
            onDone={() => onOpenChange(false)}
            listTestId="shell-settings-security-recovery-codes"
            copyTestId="shell-settings-security-recovery-copy"
            confirmTestId="shell-settings-security-recovery-confirm"
            doneTestId="shell-settings-security-recovery-done"
          />
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => void onRegenerate(e)}
            noValidate
          >
            <OtpCodeInput
              id="mfa-regen-code"
              label="Current authenticator code"
              value={code}
              onChange={setCode}
              invalid={error !== null}
              disabled={submitting}
              autoFocus
              data-testid="shell-settings-security-regenerate-code"
            />
            <DialogFooter>
              <Button
                type="submit"
                variant="primary"
                size="md"
                data-testid="shell-settings-security-regenerate-confirm"
                disabled={submitting}
              >
                {submitting ? "Generating…" : "Generate new codes"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
